import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SorteDeLien } from '@nextilt/commun';
import { api, EchecApi } from '../../partage/api/client';
import { IconeCroix, IconeInverser, IconeOeil, IconeOeilBarre } from '../../partage/icones';
import { relireApres } from '../../partage/relectures';
import { useEchap } from '../../partage/useEchap';
import {
  ANNULER,
  CACHER_LE_LIEN,
  INVERSE_FERMERAIT_UNE_BOUCLE,
  INVERSER,
  INVERSER_CE_LIEN_QUESTION,
  INVERSER_LE_LIEN,
  LIEN_PAS_CHANGE,
  MONTRER_LE_LIEN,
  SUPPRIMER,
  SUPPRIMER_CE_LIEN_QUESTION,
  SUPPRIMER_LE_LIEN,
} from '../../partage/vocabulaire';
import { signesDuLien } from './signesDuLien';
import './GestesDuLien.css';

/** Ce que les signes lisent d'un lien : un trait de la carte, une ligne de la fiche Plan. */
export interface LienASigner {
  /** Nul : un trait qui réunit plusieurs liens, qui n'a pas de signes. */
  lienId: string | null;
  sorte: SorteDeLien;
  visible: boolean;
}

/**
 * **Les trois signes d'un lien** (chantier liens-entre-bulles, tickets 03 et 05, décision 11 du
 * cadrage), écrits une fois pour la carte (`SignesDuLien`, au milieu d'un trait cliqué) et la
 * fiche Plan (sur chaque ligne) : l'œil cache le lien, ou le montre s'il est caché, sans
 * confirmation ; ⇄ l'inverse, pour « attend » et « sert à » seulement ; la croix le supprime.
 * Inverser et supprimer demandent une confirmation, qui remplace les signes à leur place.
 *
 * Le geste relit ce que poser ou retirer une condition relit : la carte, la fiche, Maintenant.
 * `sansCroix` : une condition posée dans la fiche Plan, qui garde son « Retirer ».
 * `echapAnnule` : Échap range la confirmation avant de fermer ce qui la porte (la fiche) ; sur la
 * carte, la boîte des signes se ferme d'un coup, comme avant.
 */
export function GestesDuLien({
  lien,
  onFait,
  sansCroix = false,
  echapAnnule = false,
}: {
  lien: LienASigner;
  /** Le geste a réussi : la carte ferme sa boîte ; la fiche n'a rien à fermer. */
  onFait?: () => void;
  sansCroix?: boolean;
  echapAnnule?: boolean;
}) {
  const { lienId } = lien;
  const client = useQueryClient();
  const [confirmer, setConfirmer] = useState<'inverser' | 'croix' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Une confirmation au-dessus de la fiche : Échap la range, la fiche reste (`garde`).
  useEchap(echapAnnule && confirmer !== null, () => setConfirmer(null), 'garde');

  const apres = {
    onSuccess: () => {
      setConfirmer(null);
      void relireApres(client, 'poserOuRetirerUneCondition');
      onFait?.();
    },
    onError: (erreur: unknown) => {
      setConfirmer(null);
      setMessage(erreur instanceof EchecApi && erreur.code === 'cycle' ? INVERSE_FERMERAIT_UNE_BOUCLE : LIEN_PAS_CHANGE);
    },
  };
  const voir = useMutation({ mutationFn: (visible: boolean) => api.montrerLeLien(lienId ?? '', visible), ...apres });
  const inverser = useMutation({ mutationFn: () => api.inverserLeLien(lienId ?? ''), ...apres });
  const supprimer = useMutation({ mutationFn: () => api.delier(lienId ?? ''), ...apres });
  const enCours = voir.isPending || inverser.isPending || supprimer.isPending;

  if (lienId === null) return null;

  return (
    <>
      {confirmer === null ? (
        <div className="signes-du-lien-boutons">
          {signesDuLien(lien.sorte, { sansCroix }).map((signe) =>
            signe === 'oeil' ? (
              <button
                key={signe}
                type="button"
                className="signes-du-lien-signe"
                data-signe="oeil"
                aria-label={lien.visible ? CACHER_LE_LIEN : MONTRER_LE_LIEN}
                title={lien.visible ? CACHER_LE_LIEN : MONTRER_LE_LIEN}
                disabled={enCours}
                onClick={() => {
                  setMessage(null);
                  voir.mutate(!lien.visible);
                }}
              >
                {lien.visible ? <IconeOeilBarre className="signes-du-lien-icone" /> : <IconeOeil className="signes-du-lien-icone" />}
              </button>
            ) : (
              <button
                key={signe}
                type="button"
                className="signes-du-lien-signe"
                data-signe={signe}
                aria-label={signe === 'inverser' ? INVERSER_LE_LIEN : SUPPRIMER_LE_LIEN}
                title={signe === 'inverser' ? INVERSER_LE_LIEN : SUPPRIMER_LE_LIEN}
                disabled={enCours}
                onClick={() => {
                  setMessage(null);
                  setConfirmer(signe);
                }}
              >
                {signe === 'inverser' ? <IconeInverser className="signes-du-lien-icone" /> : <IconeCroix className="signes-du-lien-icone" />}
              </button>
            )
          )}
        </div>
      ) : (
        <div className="signes-du-lien-confirmer" role="alertdialog" aria-label={confirmer === 'inverser' ? INVERSER_CE_LIEN_QUESTION : SUPPRIMER_CE_LIEN_QUESTION}>
          <span>{confirmer === 'inverser' ? INVERSER_CE_LIEN_QUESTION : SUPPRIMER_CE_LIEN_QUESTION}</span>
          <button
            type="button"
            className="bouton"
            data-confirmer={confirmer}
            disabled={enCours}
            onClick={() => (confirmer === 'inverser' ? inverser.mutate() : supprimer.mutate())}
          >
            {confirmer === 'inverser' ? INVERSER : SUPPRIMER}
          </button>
          <button type="button" className="bouton bouton-discret" onClick={() => setConfirmer(null)}>
            {ANNULER}
          </button>
        </div>
      )}
      {message && (
        <p className="signes-du-lien-message" role="status">
          {message}
        </p>
      )}
    </>
  );
}
